import { useFrame, useThree } from '@react-three/fiber';
import React, { ReactElement, useRef, FunctionComponent, Suspense, useEffect, useLayoutEffect, useState, useMemo } from 'react';
import { DoubleSide, Euler, Group, Matrix4, Quaternion, Texture, Vector3 } from 'three';
import { ISceneActions, useDataStore, useSceneStore } from '../../../zustand';
import { Jute } from '../../..';
import PlacementScene from '../PlacementScene';
import { IActionCategory, IContent360Scene, IPlaySoundAction, IRoot, ISceneComp, ITrackingTypes } from '../../r3f-components/component-data-structure';
import { presetAnimationController } from '../../PresetAnimationController';
import Controller from './Controller';
import ShadowPlane from '../../r3f-components/components/ShadowPlane';
import { useRefState } from '../../../hooks';
import { useControllerIntersections } from './webxr-hooks';
import Cursor from './Cursor';
import { transformPlacementGroup, onSelectCb, onSelectEndCb, onSelectStartCb, enterSceneById, resetSceneById, getInvertEulerRotationFromDegreeTuple } from './webxr-utils';
import { useGetActiveTrackingType } from '../../hoc/hocCustomHooks';
import { useGetCurrentSceneId, useGetFirstSceneId, useGetPreviousSceneId } from '../../../hooks/general';
import Background360 from '../../r3f-components/components/Background360';
import LoadingSphere from '../../r3f-components/components/LoadingSphere';
import { filestore, FsImage360, FsVideo360 } from '@/filestore';

type IParentProps = {
	enabled: boolean;
    onShowContentFirstTime?: () => any;
	children?: ReactElement[] | undefined;
};

const tempPositionLeft = new Vector3();
const tempPositionRight = new Vector3();
const initialDirectionUnit = new Vector3();
const tempDirection = new Vector3();
const tempScale = new Vector3();
const tempQuaternion2 = new Quaternion();
const tempEuler = new Euler();
const horizontalPlaneNormal = new Vector3(0, 1, 0);

const WebXR: FunctionComponent<IParentProps> = ({children, enabled, onShowContentFirstTime}) => {
    const { gl, scene: threeScene, raycaster, camera } = useThree();
    const activeSceneId = useSceneStore(({ activeSceneId }) => activeSceneId);
    const componentsById = useDataStore(({componentsById}) => componentsById);
    const tempScaleMatrix = useRef(new Matrix4());
    const tempVector3 = useRef(new Vector3());
    const tempQuaternion = useRef(new Quaternion());
    const tempRotationMatrix = useRef(new Matrix4())
    const [activeSelectHand, setActiveSelectHand, activeSelectHandRef] = useRefState<XRHandedness[]>([]);
    const [activeGripHand, setActiveGripHand, activeGripHandRef] = useRefState<XRHandedness[]>([]);
    const placementMode = useSceneStore(({ placementMode }) => placementMode);
    const sceneActionDispatch = useSceneStore(({dispatch}) => dispatch)

    const activeTrackingType = useGetActiveTrackingType();
    const currentSceneId = useGetCurrentSceneId();
    const previousSceneId = useGetPreviousSceneId();
    const firstSceneId = useGetFirstSceneId();
    const isSessionSetupRef = useRef(false);

    const cursorGroupRightRef = useRef<Group>(null);
    const cursorGroupLeftRef = useRef<Group>(null);
    // const hitTestPlaneRef = useRef<Mesh>(null);

    const previousAngleRef = useRef<number | null>(null);
    const controllerGroupLeftRef = useRef<Group>(null!)
    const controllerGroupRightRef = useRef<Group>(null!)
    const _currentSceneId = activeSceneId || firstSceneId;
    const placementGroupRef = useRef<Group>(null!);
    const arContentGroupRef = useRef<Group>(null);
    const initialDistanceRef = useRef<number | null>(undefined);
    const [session, setSession] = useState<XRSession>();
    const selectHasRunRef = useRef<boolean>(false);
    const selectStartHasRunRef = useRef<boolean>(false);
    const selectEndHasRunRef = useRef<boolean>(false);
    const [inputSources, setInputSources] = useState(new Array(...session?.inputSources ?? []).map(source => source.handedness));
    const [content360Media, setContent360Media] = useState<{ imageTexture?: Texture, videoUrl?: string }>({});
    
    const scene = useMemo(() => componentsById?.[_currentSceneId], [_currentSceneId, componentsById]) as (ISceneComp & IContent360Scene);
    const sceneOnLoadPlaySoundActions = useMemo(() => scene?.actions?.onLoad?.filter(action => action.type === IActionCategory.playSound), [scene]) as IPlaySoundAction[];

    const [placementController, setPlacementController] = useState<XRHandedness>(inputSources.length === 1 ? 'left' : 'right')

    useEffect(() => {
        session?.addEventListener('inputsourceschange', (e) => {
            if (e.added.length) {
                const addedHands = e.added.map(source => source.handedness);
                setInputSources(prev => [...prev, ...addedHands]);
            }
            if (e.removed.length) {
                const removedHands = e.removed.map(source => source.handedness);
                setInputSources(prev => prev.filter(hand => !removedHands.includes(hand)));
            }
        })
    }, [session])

    useEffect(() => {
        if (inputSources.length === 1) {
            setPlacementController('left');
            return;
        }
        setPlacementController('right');
    }, [inputSources])
    
    useEffect(() => {
        if (!placementMode) return;
        selectHasRunRef.current = false;
        selectStartHasRunRef.current = false;
        selectEndHasRunRef.current = false;
    }, [placementMode])

    useEffect(() => {
        if (activeTrackingType !== ITrackingTypes.content360) return;

        useSceneStore.dispatch({
            type: ISceneActions.SET_PLACEMENT_MODE,
            placementMode: false
        });
        setTimeout(() => {               
            if (arContentGroupRef.current) arContentGroupRef.current.visible = true;
            if (placementGroupRef.current) placementGroupRef.current.visible = false;
        }, 0);
    }, [previousSceneId, activeTrackingType])

    useEffect(() => {
        // Only set up session if current scene is the first scene and session has not been set up before


        if (isSessionSetupRef.current || currentSceneId !== firstSceneId) return;

        isSessionSetupRef.current = true;
        const setupXR = async(_session: XRSession) => {
            gl.xr.setReferenceSpaceType(Jute.isPico ? 'local-floor' : 'local');
            gl.xr.setSession(_session);
            setSession(_session);
            
            onShowContentFirstTime?.();
            Jute.firstCameraFrame();
            if (activeTrackingType !== ITrackingTypes.content360) {
                activatePlacementMode(placementGroupRef.current, arContentGroupRef.current, firstSceneId, false);
                return
            };
            enterSceneById(currentSceneId, componentsById, presetAnimationController, sceneOnLoadPlaySoundActions);
        }

        if (Jute.session) setupXR(Jute.session);
        else Jute.onSessionStarted(setupXR);
    }, [currentSceneId, firstSceneId, activeTrackingType, componentsById])

    useLayoutEffect(() => {
        // Always immediately enter 360 content scenes
        if (activeTrackingType !== ITrackingTypes.content360 || !isSessionSetupRef.current || !componentsById) return;

        resetSceneById(currentSceneId, componentsById, presetAnimationController, threeScene);
        enterSceneById(currentSceneId, componentsById, presetAnimationController, sceneOnLoadPlaySoundActions);

        if (placementGroupRef.current) placementGroupRef.current.visible = false;
        if (arContentGroupRef.current) arContentGroupRef.current.visible = true;
    }, [activeTrackingType, componentsById])

    useEffect(() => {
        if (activeTrackingType !== ITrackingTypes.content360) return;
        

        if (!scene.content360?.initialCameraRotation) return;
        const { initialCameraRotation } = scene.content360;
        const { x, y, z } = getInvertEulerRotationFromDegreeTuple(initialCameraRotation)
        arContentGroupRef.current!.rotation.set(x, y, z);
    }, [activeSceneId])


    useFrame(({camera}) => {
        if (activeTrackingType !== ITrackingTypes.content360) return;
        arContentGroupRef.current?.position.copy(camera.position);
    })

    const activatePlacementMode = (placementGroup: Group | undefined, arContentGroup: Group | null, currentSceneId: string, isPlacementSceneVisible = true) => {
        if (!placementGroup || !arContentGroup) return;
        placementGroup.visible = isPlacementSceneVisible; 
        arContentGroup.visible = false;

        resetSceneById(currentSceneId, componentsById, presetAnimationController, threeScene)

        useSceneStore.dispatch({
            type: ISceneActions.SET_PLACEMENT_MODE,
            placementMode: true
        })
    }

    const onButtonPressDown = (index: number, handedness: XRHandedness, controllerGroup: Group) => {        
        const currentPlacementMode = useSceneStore.getState().placementMode;
        const arContentGroup = arContentGroupRef.current;
        const placementGroup = placementGroupRef.current;
        const activeSceneId = useSceneStore.getState().activeSceneId;
        const rootId = useDataStore.getState().rootId;
        const componentsById = useDataStore.getState().componentsById;
        const firstSceneId = (componentsById?.[rootId ?? ''] as IRoot).children[0];
        const currentSceneId = activeSceneId ?? firstSceneId;

        setPlacementController(handedness);

        if (index === 0 || index === 4)  {
            // Select entities if not in placement mode
            if (!currentPlacementMode) {
                onSelectStartCb({ raycaster, handedness, activeSelectHandRef, setActiveSelectHand, selectStartHasRunRef, controllerGroup, sceneId: currentSceneId })
                onSelectCb({raycaster, selectHasRunRef, controllerGroup, sceneId: currentSceneId})
            // Exit placement mode if in placement mode
            } else if (currentPlacementMode && placementGroup && arContentGroup) {
                // start enter transition for current scene once scene has been placed
                useSceneStore.dispatch({
                    type: ISceneActions.SET_PLACEMENT_MODE,
                    placementMode: false
                });

                if (activeTrackingType !== ITrackingTypes.content360) placementGroup.matrix.decompose( arContentGroup.position, arContentGroup.quaternion, arContentGroup.scale ); 

                enterSceneById(currentSceneId, componentsById, presetAnimationController, sceneOnLoadPlaySoundActions);

                setTimeout(() => {
                    placementGroup.visible = false; 
                    arContentGroup.visible = true;   
                }, 0);

                return;
            }

        }

        if (currentPlacementMode || activeTrackingType === ITrackingTypes.content360) return;

        if (index === 1 && !activeGripHandRef.current.includes(handedness)) {
            setActiveGripHand([...activeGripHandRef.current, handedness])
        }

        // Enter placement mode
        if (index === 5 && arContentGroup && placementGroup) {
            activatePlacementMode(placementGroup, arContentGroup, currentSceneId);
        }
    }

    const onButtonPressUp = (index: number, handedness: XRHandedness, controllerGroup: Group) => {
        // console.log('button up', handedness);
        if ((index === 0 || index === 4) && !placementMode) {
            onSelectEndCb({ raycaster, selectEndHasRunRef, activeSelectHandRef, setActiveSelectHand, handedness, controllerGroup, sceneId: currentSceneId })
        }

        if (index === 1 && activeGripHandRef.current.includes(handedness)) {
            setActiveGripHand(activeGripHandRef.current.filter(handedness => handedness !== handedness))
        }
        if (placementGroupRef.current) placementGroupRef.current.visible = false; 
        if (arContentGroupRef.current) arContentGroupRef.current.visible = true;
    }

    const onXAxisChange = (value: number) => {
        if (!placementMode || activeTrackingType === ITrackingTypes.content360) return;
        tempQuaternion.current.setFromRotationMatrix(tempRotationMatrix.current);
        const { y } = new Euler().setFromQuaternion(tempQuaternion.current);
        let _value = Math.sign(value) / 10 + y;

        const euler = new Euler(0, _value, 0);
        tempRotationMatrix.current.makeRotationFromEuler(euler)
    }

    const onYAxisChange = (value: number) => {
        if (!placementMode || activeTrackingType === ITrackingTypes.content360) return;
        const { x } = tempVector3.current.setFromMatrixScale(tempScaleMatrix.current);
        const _value = Math.sign(value) / 40 + x;
        if (_value <= 0) return;
        tempScaleMatrix.current.makeScale(_value, _value, _value);
    }

    const hasIntersectionLeft = useControllerIntersections(cursorGroupLeftRef, controllerGroupLeftRef.current);
    const hasIntersectionRight = useControllerIntersections(cursorGroupRightRef, controllerGroupRightRef.current);

    useFrame(() => {
        if (activeGripHandRef.current.length !== 2) {
            initialDistanceRef.current = null;
            previousAngleRef.current = null;
            return
        };

        if (!controllerGroupLeftRef.current || !controllerGroupRightRef.current || !arContentGroupRef.current) return;

        tempPositionLeft
            .setFromMatrixPosition(controllerGroupLeftRef.current.matrix)
        tempPositionRight
            .setFromMatrixPosition(controllerGroupRightRef.current.matrix)

        const distance = tempPositionLeft.distanceTo(tempPositionRight);

        if (initialDistanceRef.current === null) {
            tempScale.set(arContentGroupRef.current.scale.x, arContentGroupRef.current.scale.y, arContentGroupRef.current.scale.z);
            initialDistanceRef.current = distance;
            initialDirectionUnit.subVectors(tempPositionRight, tempPositionLeft).projectOnPlane(horizontalPlaneNormal).normalize(); 
        }

        const ratio = distance / initialDistanceRef.current!;
        const newScale = tempScale.clone().multiplyScalar(ratio);
        arContentGroupRef.current.scale.set(newScale.x, newScale.y, newScale.z);

        const currentDicrectionUnit = tempDirection.subVectors(tempPositionRight, tempPositionLeft).projectOnPlane(horizontalPlaneNormal).normalize();
                   
        tempQuaternion2.setFromUnitVectors(initialDirectionUnit, currentDicrectionUnit); 
        tempEuler.setFromQuaternion(tempQuaternion2);

        if (tempEuler.y !== previousAngleRef.current) {
            arContentGroupRef.current.rotateY(tempEuler.y / 50);
            previousAngleRef.current = tempEuler.y;
        }
    })

    const onVideoNodeReady = () => {
        sceneActionDispatch({
            type: ISceneActions.SET_VIDEO_TEXTURE_AVAILABLE,
            payload: { sceneId: currentSceneId, available: true }
        })
    }

    useEffect(() => {
        if (typeof scene.content360?.filestoreId === 'undefined') return;
        if (scene.content360.type === 'image') {
            const imgFile = filestore.get<FsImage360>(scene.content360.filestoreId)
            imgFile.getTexture().then(txt => {
                setContent360Media({ imageTexture: txt, videoUrl: undefined })
            })
        } else {
            setContent360Media({
                imageTexture: undefined,
                videoUrl: filestore.get<FsVideo360>(scene.content360.filestoreId).getPlaylistUrl()
            })
        }
    }, [scene.content360?.filestoreId]);

    const isTextureAvailable = !!content360Media.imageTexture || !!content360Media.videoUrl;
 
	return (
		<>
            {inputSources.includes('left') && <Controller 
                ref={controllerGroupLeftRef} 
                isPlacementMode={placementMode}
                handedness='left'
                transformActive={activeGripHand.includes('left')}
                selectActive={activeSelectHand.includes('left')}
                hitTestingActive={placementMode && placementController === 'left' && activeTrackingType !== ITrackingTypes.content360}
                intersectionDetected={hasIntersectionLeft}
                onHitDetected={hit => {
                    if (!placementGroupRef.current || activeTrackingType === ITrackingTypes.content360) return;
                    if (!placementMode) {
                        placementGroupRef.current.visible = false;
                        return;
                    }
                    transformPlacementGroup(hit, placementGroupRef, camera, tempRotationMatrix, tempScaleMatrix);
                    // placementGroupRef.current.position.setY(0)
                    if (hit.detected) placementGroupRef.current.visible = true;
                }}
                onButtonPressDown={onButtonPressDown} 
                onButtonPressUp={onButtonPressUp} 
                onXAxisChange={onXAxisChange}
                onYAxisChange={onYAxisChange}
                showButtonLabel={activeTrackingType !== ITrackingTypes.content360}
                space='target' 
                showRay={placementMode && placementController === 'left' || !placementMode}
            />}

            {inputSources.includes('right') && <Controller 
                ref={controllerGroupRightRef} 
                handedness='right'
                isPlacementMode={placementMode}
                transformActive={activeGripHand.includes('right')}
                selectActive={activeSelectHand.includes('right')}
                hitTestingActive={placementMode && placementController === 'right' && activeTrackingType !== ITrackingTypes.content360}
                intersectionDetected={hasIntersectionRight}
                onHitDetected={hit => {
                    if (!placementGroupRef.current || activeTrackingType === ITrackingTypes.content360) return;
                    if (!placementMode) {
                        placementGroupRef.current.visible = false;
                        return;
                    }
                    transformPlacementGroup(hit, placementGroupRef, camera, tempRotationMatrix, tempScaleMatrix)
                    // placementGroupRef.current.position.setY(0)
                    if (hit.detected) placementGroupRef.current.visible = true;
                }}
                onButtonPressDown={onButtonPressDown} 
                onButtonPressUp={onButtonPressUp} 
                onXAxisChange={onXAxisChange}
                onYAxisChange={onYAxisChange}
                showButtonLabel={activeTrackingType !== ITrackingTypes.content360}
                space='target' 
                showRay={placementMode && placementController === 'right' || !placementMode}
            />}

            <Cursor ref={cursorGroupLeftRef} visible={hasIntersectionLeft && !activeGripHand.includes('left')} handedness={'left'} activeSelectHand={activeSelectHand} />
            <Cursor ref={cursorGroupRightRef} visible={hasIntersectionRight && !activeGripHand.includes('right')} handedness={'right'} activeSelectHand={activeSelectHand} />

            <group ref={placementGroupRef} visible={false} matrixAutoUpdate={false} >
                {activeTrackingType !== ITrackingTypes.content360 && 
                    <>
                        <PlacementScene sceneId={_currentSceneId} />
                        <mesh position={[0, 0, 0.001]} rotation={[Math.PI / 2, 0, 0]} >
                            <ringGeometry args={[0.25 / 2, 0.28 / 2, 50 / 2]} />
                            <meshBasicMaterial color={0xffffff} side={DoubleSide} />
                        </mesh>
                        {placementMode && <ShadowPlane rotation={[-Math.PI / 2, 0, 0]} occlusion opacity={0.3} />}
                    </>
                }
            </group>
   
            <group ref={arContentGroupRef} visible={false} >
                {children}
                {!!scene.content360?.filestoreId && isTextureAvailable ? 
                    <Suspense fallback={<LoadingSphere radius={60} renderOrder={-99999999999}/>}>
                        <Background360
                            media={content360Media}
                            radius={60}
                            onVideoNodeReady={onVideoNodeReady}
                            type={scene.content360.type}
                            environmentRenderPriority={11}
                            sceneId={currentSceneId}
                            loop={scene.content360.type === 'video' ? scene.content360.loop : undefined}
                            muted={scene.content360.type === 'video' ? scene.content360.muted : undefined}
                            addVideoToDom={Jute.isAppClip}
                        />
                    </Suspense>
                    : null
                }
                {!placementMode && activeTrackingType !== ITrackingTypes.content360 && <ShadowPlane rotation={[-Math.PI / 2, 0, 0]} occlusion opacity={0.3} />}
            </group>
            {Jute.isPico && <mesh name='ground-plane' visible={false} rotation={[Math.PI / 2, 0, 0]} >
                <planeGeometry args={[100, 100]}/>
                <meshBasicMaterial color={'green'} side={DoubleSide}/>
            </mesh>}
		</>
	);
};

WebXR.displayName = 'WebXR';
export default WebXR;